import { supabase } from './supabaseClient';
import { ProjectSplit, ProjectSplitParticipant, SHARE_FULL_BP } from './projectSplit';

/**
 * Datenschicht zum Aufteilungsschluessel (supabase/project_splits.sql,
 * SQL Nr. 51).
 *
 * Bewusst duenn: gerechnet wird in projectSplit.ts, hier wird nur gelesen und
 * geschrieben. Der Rechenkern bleibt dadurch ohne Supabase testbar — dieselbe
 * Arbeitsteilung wie zwischen invoiceLogic und invoiceService.
 *
 * Stil wie crmService/invoiceService: async, throw bei Fehler,
 * snake_case <-> camelCase nur in den Mappern, damit der Rest der Anwendung
 * ausschliesslich camelCase sieht.
 *
 * Was hier NICHT passiert: die Pruefsumme. `validateSplit` braucht die Liste
 * der LV-Positionen, und die kennt nur der Aufrufer — eine Position ganz ohne
 * Ausnahme faellt sonst aus der Pruefung, obwohl gerade sie die Grundquote
 * traegt. Geprueft wird deshalb im Formular und beim Ausstellen. Was hier sehr
 * wohl passiert, ist die Wertebereichspruefung (siehe assertShares): sie
 * braucht keine Positionsliste, und sie haelt Muell aus der Tabelle heraus,
 * bevor die Datenbank-CHECKs ihn mit einer englischen Meldung ablehnen.
 */

// ─── Row-Mapper ─────────────────────────────────────────────────────────────

interface SplitRow {
    id: string;
    project_id: string;
    customer_id: string;
    default_share_bp: number | string;
    /** Der Abschnitt. Fehlt die Spalte (SQL Nr. 54 nicht gefahren), ist sie leer. */
    label?: string | null;
}

interface OverrideRow {
    split_id: string;
    service_id: string;
    share_bp: number | string;
}

const SPLIT_COLUMNS = 'id, project_id, customer_id, default_share_bp, label';
const OVERRIDE_COLUMNS = 'split_id, service_id, share_bp';

/**
 * Groesse eines ID-Blocks fuer `.in(...)`-Filter — dieselbe Grenze und
 * dieselbe Begruendung wie in invoiceService: der Filter steht in der URL, ein
 * zu langer Filter wird abgeschnitten und findet ZU WENIG, und zwar ohne
 * Fehler. Hier hiesse "zu wenig" ein Teilnehmer, der aus dem Schluessel faellt
 * — die uebrigen kaemen dann in der Summe nicht mehr auf 100 %, jeder einzelne
 * Beleg saehe aber richtig aus.
 */
const ID_CHUNK = 100;

const chunked = <T,>(items: T[], size: number): T[][] => {
    const out: T[][] = [];
    for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
    return out;
};

/** Postgres liefert `integer` je nach Treiber als Zahl oder als Text. */
const bp = (value: number | string): number => Number(value) || 0;

const uniqueIds = (values: (string | undefined | null)[]): string[] => {
    const set = new Set<string>();
    for (const value of values) {
        const id = (value || '').trim();
        if (id) set.add(id);
    }
    return [...set];
};

// ─── Lesen ──────────────────────────────────────────────────────────────────

/**
 * Aufteilungsschluessel mehrerer Projekte in ZWEI Abfragen, unabhaengig von
 * der Anzahl der Projekte und Teilnehmer.
 *
 * Gebuendelt und nicht je Projekt: Projektlisten und der Abrechnungslauf
 * fragen den Schluessel fuer dutzende Projekte nacheinander ab. Eine Abfrage je
 * Projekt (oder gar je Teilnehmer) waere genau das N+1, an dem der
 * Abrechnungslauf schon einmal in serielle Roundtrips zerfallen ist.
 *
 * Projekte ohne Teilnehmer stehen NICHT in der Karte. Der Aufrufer soll
 * "kein Schluessel" und "leerer Schluessel" nicht unterscheiden muessen; ein
 * Eintrag mit leerer Teilnehmerliste waere zudem ueber `hasSplit` ohnehin
 * kein Split.
 */
export const fetchProjectSplits = async (
    projectIds: string[],
): Promise<Map<string, ProjectSplit>> => {
    const out = new Map<string, ProjectSplit>();
    const ids = uniqueIds(projectIds);
    if (ids.length === 0) return out;

    const splitRows: SplitRow[] = [];
    for (const block of chunked(ids, ID_CHUNK)) {
        const { data, error } = await supabase
            .from('project_splits')
            .select(SPLIT_COLUMNS)
            .in('project_id', block);
        if (error) throw error;
        splitRows.push(...((data ?? []) as SplitRow[]));
    }
    if (splitRows.length === 0) return out;

    // Die Ausnahmen aller gefundenen Teilnehmer in EINEM Zug — nicht je
    // Teilnehmer. Bei drei Auftraggebern auf dreissig Projekten waeren das
    // sonst neunzig Abfragen fuer eine Tabelle mit ein paar hundert Zeilen.
    const overrideRows: OverrideRow[] = [];
    for (const block of chunked(splitRows.map(row => row.id), ID_CHUNK)) {
        const { data, error } = await supabase
            .from('project_split_overrides')
            .select(OVERRIDE_COLUMNS)
            .in('split_id', block);
        if (error) throw error;
        overrideRows.push(...((data ?? []) as OverrideRow[]));
    }

    const overridesBySplit = new Map<string, Record<string, number>>();
    for (const row of overrideRows) {
        const map = overridesBySplit.get(row.split_id) ?? {};
        map[row.service_id] = bp(row.share_bp);
        overridesBySplit.set(row.split_id, map);
    }

    for (const row of splitRows) {
        const split = out.get(row.project_id) ?? { projectId: row.project_id, participants: [] };
        const participant: ProjectSplitParticipant = {
            // Die Zeilenkennung IST die Identitaet des Teilnehmers. Sie wird
            // beim Speichern zurueckgereicht; nur so ueberlebt ein Teilnehmer,
            // dessen Auftraggeber oder dessen Abschnittsname korrigiert wird,
            // samt seiner Ausnahmen je LV-Position.
            partyId: row.id,
            customerId: row.customer_id,
            defaultShareBp: bp(row.default_share_bp),
        };
        // Das Feld bleibt WEG, wenn kein Abschnitt gepflegt ist — aus
        // demselben Grund wie bei `overrides` unten: ein Vergleich zweier
        // Schluessel soll nicht an einem leeren Text scheitern, der nichts
        // bedeutet.
        const label = (row.label || '').trim();
        if (label) participant.label = label;
        // Das Feld bleibt WEG, wenn es keine Ausnahmen gibt (und nicht als
        // leeres Objekt stehen): `shareBpFor` fragt mit `!== undefined` ab,
        // und ein Vergleich zweier Schluessel (Formular gegen Datenbank) soll
        // nicht an einem leeren Objekt scheitern, das nichts bedeutet.
        const overrides = overridesBySplit.get(row.id);
        if (overrides && Object.keys(overrides).length > 0) participant.overrides = overrides;
        split.participants.push(participant);
        out.set(row.project_id, split);
    }

    /*
     * Feste Reihenfolge nach Kundenkennung.
     *
     * PostgREST gibt ohne `order` keine zugesicherte Reihenfolge zurueck. Die
     * Teilnehmerreihenfolge steuert aber, in welcher Folge `splitLines` die
     * Teilbelege aufbaut — und ein Beleg, der sich beim erneuten Ansehen
     * anders zusammensetzt, ist kein Beleg. Dieselbe Ueberlegung wie bei der
     * Restvergabe in `splitUnits`.
     */
    // Feste Reihenfolge: erst der Abschnitt, dann der Auftraggeber. Der
    // Abschnitt fuehrt, weil er die Ordnung ist, in der der Betrieb denkt
    // (Haus 1, Haus 2, Haus 3) — und weil bei mehreren Abschnitten desselben
    // Auftraggebers die Kundenkennung gar nicht mehr sortiert. Die
    // Teilnehmerkennung entscheidet den Gleichstand, damit die Ordnung auch
    // dann total ist.
    const byLabelThenCustomer = (a: ProjectSplitParticipant, b: ProjectSplitParticipant): number =>
        (a.label || '').localeCompare(b.label || '', 'de', { numeric: true }) ||
        (a.customerId < b.customerId ? -1 : a.customerId > b.customerId ? 1 : 0) ||
        (a.partyId < b.partyId ? -1 : a.partyId > b.partyId ? 1 : 0);
    for (const split of out.values()) {
        split.participants.sort(byLabelThenCustomer);
    }
    return out;
};

/**
 * Der Aufteilungsschluessel EINES Projekts, oder `null`, wenn keiner
 * hinterlegt ist.
 *
 * Laeuft ueber dieselbe gebuendelte Abfrage wie die Mehrfachvariante: ein
 * zweiter Lesepfad waere eine zweite Gelegenheit, die Abbildung der Ausnahmen
 * unterschiedlich zu bauen.
 */
export const fetchProjectSplit = async (projectId: string): Promise<ProjectSplit | null> => {
    const id = (projectId || '').trim();
    if (!id) return null;
    const map = await fetchProjectSplits([id]);
    return map.get(id) ?? null;
};

// ─── Pruefung vor dem Schreiben ─────────────────────────────────────────────

/**
 * Wertebereich der Anteile: ganze Basispunkte von 0 bis 10000.
 *
 * Warum hier und nicht erst in der Datenbank: die CHECK-Bedingungen der
 * Tabelle fangen den Fall zwar ab, melden ihn aber als englischen
 * Constraint-Verstoss ("violates check constraint
 * project_splits_default_share_bp_check"), und zwar erst nachdem ein Teil des
 * Abgleichs schon geschrieben ist. Der Bruchteil-Fall kaeme dort ueberhaupt
 * nicht an: 50,5 Basispunkte werden von Postgres auf `integer` GERUNDET und
 * stillschweigend angenommen — der Schluessel summierte sich danach auf 9999
 * oder 10001 und verschenkte oder forderte dauerhaft ein Hundertstel Prozent.
 *
 * Ausdruecklich NICHT geprueft wird die Pruefsumme (Summe je Position = 100 %).
 * Dafuer fehlt hier die Positionsliste, siehe Kopfkommentar.
 */
const assertShares = (split: ProjectSplit): void => {
    if (!split || !(split.projectId || '').trim()) {
        throw new Error('Der Aufteilungsschlüssel nennt kein Projekt.');
    }

    const seen = new Set<string>();
    for (const participant of split.participants ?? []) {
        const customerId = (participant?.customerId || '').trim();
        if (!customerId) {
            throw new Error('Ein Eintrag im Aufteilungsschlüssel nennt keinen Auftraggeber.');
        }
        // Derselbe Auftraggeber im SELBEN Abschnitt bekaeme zwei Teilrechnungen
        // ueber denselben Anteil. Die Datenbank hat dafuer den eindeutigen
        // Index (project_id, customer_id, label), der Abgleich unten aber
        // fuehrt seine Karten ueber dieses Paar — der zweite Eintrag
        // ueberschriebe den ersten lautlos.
        //
        // In VERSCHIEDENEN Abschnitten ist derselbe Auftraggeber dagegen der
        // Regelfall (ein Gesamtprojekt, mehrere Haeuser, ein Bauherr) und
        // ausdruecklich erlaubt.
        const label = (participant?.label || '').trim();
        const pair = `${customerId}|${label}`;
        if (seen.has(pair)) {
            throw new Error(
                label
                    ? `Der Auftraggeber steht im Abschnitt „${label}" mehrfach im Aufteilungsschlüssel.`
                    : 'Derselbe Auftraggeber steht mehrfach im Aufteilungsschlüssel. Sollen es mehrere Abschnitte sein, braucht jeder einen eigenen Namen.',
            );
        }
        seen.add(pair);

        const shares: [string, unknown][] = [['Grundquote', participant.defaultShareBp]];
        for (const [serviceId, value] of Object.entries(participant.overrides ?? {})) {
            if (!(serviceId || '').trim()) {
                throw new Error('Eine Ausnahme im Aufteilungsschlüssel nennt keine LV-Position.');
            }
            shares.push([`Ausnahme zu Position ${serviceId}`, value]);
        }

        for (const [label, value] of shares) {
            if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > SHARE_FULL_BP) {
                throw new Error(
                    `Ungültiger Anteil (${label}: ${String(value)}). Erlaubt sind ganze Basispunkte von 0 bis ${SHARE_FULL_BP}.`,
                );
            }
        }
    }
};

// ─── Schreiben ──────────────────────────────────────────────────────────────

/**
 * Schreibt Teilnehmer und Ausnahmen eines Projekts — als ABGLEICH gegen den
 * Bestand, nicht als blindes Einfuegen.
 *
 * TRAGEND: ein bestehender Teilnehmer wird AKTUALISIERT und niemals geloescht
 * und neu angelegt. An `project_splits.id` haengen seine Ausnahmen
 * (`project_split_overrides.split_id ... on delete cascade`). Wer die Zeile
 * neu anlegt, bekommt eine neue id — und die Kaskade raeumt vorher saemtliche
 * Ausnahmen dieses Teilnehmers ab. Wer positionsweise aufteilt, traegt seine
 * gesamte Zuordnung genau dort; nach einem Neuanlegen faellt er auf die
 * Grundquote zurueck, die in diesem Fall bewusst 0 ist. Der Auftraggeber
 * verschwaende damit aus jeder Position, ohne dass irgendwo etwas auffiele —
 * bis die uebrigen Belege in der Summe nicht mehr 100 % fordern.
 *
 * Deshalb `upsert` mit `onConflict: project_id,customer_id`: Postgres macht
 * daraus INSERT ... ON CONFLICT DO UPDATE, die vorhandene Zeile behaelt ihre
 * id (und damit ihre Ausnahmen), und der ganze Block geht in EINEM Roundtrip
 * raus statt in einem je Teilnehmer.
 *
 * Eine leere Teilnehmerliste loescht den Schluessel — sie ist die einzige Art,
 * wie das Formular "dieses Projekt hat doch nur einen Auftraggeber" ausdruecken
 * kann.
 */
export const saveProjectSplit = async (split: ProjectSplit): Promise<ProjectSplit> => {
    assertShares(split);

    const projectId = split.projectId.trim();
    const participants = (split.participants ?? []).map((p) => ({
        // Die Kennung geht MIT. Ohne sie faende die Funktion den Teilnehmer nur
        // ueber (Auftraggeber, Abschnitt) — und beim Umbenennen eines
        // Abschnitts fiele sie auf "neuer Teilnehmer" zurueck: die Kaskade
        // raeumte die Ausnahmen des alten ab, und er verschwaende aus jeder
        // Position, ohne dass eine Zahl falsch aussieht.
        partyId: (p.partyId || '').trim(),
        customerId: p.customerId.trim(),
        label: (p.label || '').trim(),
        defaultShareBp: p.defaultShareBp,
        overrides: { ...(p.overrides ?? {}) },
    }));

    // EIN Aufruf, EINE Transaktion.
    //
    // Der Abgleich besteht aus vier Schritten: weggefallene Teilnehmer
    // loeschen, verbliebene aktualisieren, weggefallene Ausnahmen loeschen,
    // neue schreiben. Vom Client aus waeren das vier Requests ohne gemeinsame
    // Klammer — bricht der zweite ab, bleibt ein Schluessel stehen, dem ein
    // Teilnehmer fehlt. Der summiert sich dauerhaft auf weniger als 100 %, und
    // jede Teilrechnung daraus fordert lautlos zu wenig: jeder einzelne Beleg
    // ist dabei fuer sich richtig, nur ihre Summe nicht.
    //
    // Der Rumpf einer Postgres-Funktion laeuft in einer Transaktion. Schlaegt
    // etwas fehl, ist nichts geschrieben.
    const { error } = await supabase.rpc('save_project_split_tx', {
        p_project_id: projectId,
        p_participants: participants,
    });
    if (error) {
        // Kein stiller Rueckfall auf den Einzelschritt-Pfad, obwohl das Repo
        // ihn anderswo kennt (save_project_children_tx): dieser Rueckfall WAERE
        // genau der Zustand, gegen den die Funktion gebaut ist. Lieber gar
        // nicht speichern als halb.
        if ((error as { code?: string }).code === 'PGRST202') {
            throw new Error(
                'Der Aufteilungsschlüssel kann nicht gespeichert werden: die Datenbankfunktion save_project_split_tx fehlt. Bitte SQL Nr. 52 (supabase/save_project_split_tx.sql) und danach Nr. 54 (supabase/project_split_sections.sql) einspielen.',
            );
        }
        throw error;
    }

    // Zurueckgegeben wird der GELESENE Stand und nicht die Eingabe. Was der
    // Aufrufer anschliessend gegen die Pruefsumme haelt, soll das sein, was in
    // der Tabelle steht — nicht das, was wir hineinschreiben wollten.
    return (await fetchProjectSplit(projectId)) ?? { projectId, participants: [] };
};

/**
 * Entfernt den Aufteilungsschluessel eines Projekts vollstaendig.
 *
 * Die Ausnahmen gehen per Kaskade mit (`on delete cascade` an
 * project_split_overrides.split_id) — sie ohne ihren Teilnehmer zu behalten
 * ergaebe nichts.
 *
 * Bereits AUSGESTELLTE Teilrechnungen beruehrt das nicht: sie tragen ihre
 * Aufteilung eingefroren am Beleg (invoices.split_group_id/split_share_bp) und
 * im Leistungsprotokoll. Ein Beleg, der sich beim Loeschen des Schluessels
 * veraendert, waere kein Beleg.
 */
export const deleteProjectSplit = async (projectId: string): Promise<void> => {
    const id = (projectId || '').trim();
    if (!id) return;
    const { error } = await supabase.from('project_splits').delete().eq('project_id', id);
    if (error) throw error;
};
